import axios from "axios"
import { API_BASE_URL } from "../shared/Config"

export interface LoginTokens {
    access_token: string
    id_token: string
    refresh_token: string
    expires_in: number
    token_type: string
    email: string
}

export interface LoginChallenge {
    challenge: "TOTP" | "NEW_PASSWORD" | "UNKNOWN"
    challenge_name: string
    challenge_session: string
    challenge_parameters: Record<string, any>
}

export type LoginAuthResponse =
    | ({ kind: "tokens" } & LoginTokens)
    | ({ kind: "challenge" } & LoginChallenge)

export interface LoginAuthError {
    status: number
    messageCode: string
    details?: {
        failCount?: number
        remainingBeforeBlock?: number
        retryAfterSeconds?: number
        isBlocked?: boolean
        isPermanentlyBlocked?: boolean
    }
}

function url(poolId: string, tenant: string, path: string): string {
    return `${API_BASE_URL}/${poolId}/${tenant}/login${path}`
}

function normalizeResponseBody(data: any): LoginAuthResponse {
    if (data && data.access_token) {
        return { kind: "tokens", ...(data as LoginTokens) }
    }
    if (data && data.challenge) {
        return { kind: "challenge", ...(data as LoginChallenge) }
    }
    throw new Error("INVALID_LOGIN_RESPONSE")
}

function normalizeError(err: any): LoginAuthError {
    const status = err && err.response ? err.response.status : 0
    const body = err && err.response ? err.response.data : null
    return {
        status,
        messageCode: (body && body.message) || "UNKNOWN",
        details: body && body.details,
    }
}

async function post(endpoint: string, body: object): Promise<LoginAuthResponse> {
    try {
        const { data } = await axios.post(endpoint, body)
        return normalizeResponseBody(data)
    } catch (err) {
        throw normalizeError(err)
    }
}

export const LoginAuthService = {
    login(poolId: string, tenant: string, clientId: string, email: string, password: string): Promise<LoginAuthResponse> {
        return post(url(poolId, tenant, "/auth"), { email, password, client_id: clientId })
    },

    respondTotp(poolId: string, tenant: string, clientId: string, challengeSession: string, code: string): Promise<LoginAuthResponse> {
        return post(url(poolId, tenant, "/challenge/totp"), { client_id: clientId, challenge_session: challengeSession, code })
    },

    respondNewPassword(poolId: string, tenant: string, clientId: string, challengeSession: string, newPassword: string): Promise<LoginAuthResponse> {
        return post(url(poolId, tenant, "/challenge/new-password"), { client_id: clientId, challenge_session: challengeSession, new_password: newPassword })
    },
}
